import { put, call, select, takeLatest, delay } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import {
  ACCOUNT_GET,
  ACCOUNTS_GET,
  ACCOUNT_LIFESTYLE_COSTS_GET,
  ACCOUNT_LIFESTYLE_COSTS_POST_PATCH,
  ACCOUNT_FINANCIAL_ASSETS_GET,
  ACCOUNT_FINANCIAL_ASSETS_POST_PATCH,
  ACCOUNT_FINANCIAL_EXPERIENCES_GET,
  ACCOUNT_FINANCIAL_EXPERIENCES_POST_PATCH,
  ACCOUNT_RISK_APPETITE_PROFILES_GET,
  ACCOUNT_RISK_APPETITE_PROFILES_POST_PATCH,
  ACCOUNT_OWNERS_GET,
  ACCOUNT_ACCEPT_MIFID_PROFILE,
  ACCOUNTS_POST,
  ACCOUNT_SIMULATE_RISK_PROFILE,
  ACCOUNT_INVESTMENT_AGREEMENTS_GET,
  ACCOUNT_INVESTMENT_AGREEMENTS_POST,
  ACCOUNT_INVESTMENT_AGREEMENTS_POST_OTP,
  ACCOUNT_SIGNED_INVESTMENT_AGREEMENTS_GET,
  ACCOUNT_OWNERS_PATCH,
  ACCOUNT_AML_PROFILES_GET,
  ACCOUNT_AML_PROFILES_POST_PATCH,
  ACCOUNT_TAX_SCHEMES_GET,
  ACCOUNT_TAX_SCHEMES_POST_PATCH,
  ACCOUNT_CONFIRM_INFO,
  ACCOUNT_CONTRACTS_GET,
  ACCOUNT_CONTRACTS_POLLING_GET,
  ACCOUNT_CONTRACTS_SIGN_POST_OTP,
  ACCOUNT_DOCUMENTS_GET,
  ACCOUNT_DOCUMENTS_POST,
  ACCOUNT_AUMS_GET,
} from '../actions/types';
import {
  apiAccountFeesGet,
  apiAccountLifestyleCostsGet,
  apiAccountFinancialAssetsGet,
  apiAccountFinancialExperiencesGet,
  apiAccountRiskAppetiteProfilesGet,
  apiAccountOwnersGet,
  apiAccountFinancialAssetsPatch,
  apiAccountFinancialAssetsPost,
  apiAccountLifestyleCostsPatch,
  apiAccountLifestyleCostsPost,
  apiAccountFinancialExperiencesPatch,
  apiAccountFinancialExperiencesPost,
  apiAccountRiskAppetiteProfilePost,
  apiAccountRiskAppetiteProfilePatch,
  apiAccountRiskProfilesPost,
  apiAccountPost,
  apiAccountInvestmentAgreementsGet,
  apiAccountInvestmentAgreementsPost,
  apiAccountSignedInvestmentAgreementsGet,
  apiAccountGet,
  apiAccountOwnersPatch,
  apiAccountRiskProfileCalculationGet,
  apiAccountAmlProfilesPatch,
  apiAccountAmlProfilesPost,
  apiAccountAmlProfilesGet,
  apiAccountTaxSchemesGet,
  apiAccountTaxSchemesPatch,
  apiAccountTaxSchemesPost,
  apiAccountConfirmInfo,
  apiAccountContractsGet,
  apiAccountContractsSignPostOtp,
  apiAccountDocumentsGet,
  apiAccountDocumentsPost,
  apiAccountAumsGet,
} from '../api/account';
import { createQadraSaga, handleOtp } from './shared';
import { selectAccount } from '../selectors/accounts';
import { investmentObjectivePortfolioProposalsPost } from './investmentObjectives';
import { withId } from '../../paths';
import { otpIds } from '../../utils/otp';
import { apiUserAccountsGet } from '../api/user';
import { getServerErrorMessage } from '../../utils';
import { actionModalOpen, actionSpinnerClose, actionSpinnerOpen } from '../actions';

export function* accountsGet({ payload: { userId } }) {
  const loggedUserId = yield select((s) => s.app.user.id);
  const { accounts } = yield call(apiUserAccountsGet, userId ?? loggedUserId);
  yield put({ type: ACCOUNTS_GET._SUCCESS, payload: { accounts } });
}

const watchAccountsGet = createQadraSaga(accountsGet, { action: ACCOUNTS_GET._REQUEST });

export function* accountGet({ payload: { accountId } }) {
  const { account } = yield call(apiAccountGet, accountId);
  yield put({ type: ACCOUNT_GET._SUCCESS, payload: { accountId, account } });
}

const watchAccountGet = createQadraSaga(accountGet, { action: ACCOUNT_GET._REQUEST });

function* accountPost({
  payload: { ownerId, accountType, name, lastname, financialAdvisorId, url },
}) {
  const user = yield select((s) => s.app.user);
  ownerId = ownerId ?? user.id;

  const { account } = yield call(apiAccountPost, {
    name: `${name} ${lastname}`,
    accountTypeId: accountType,
    ownerId,
    financialAdvisorId,
    beneficiaryName: name,
    beneficiaryLastName: lastname,
  });

  yield accountsGet({ payload: { userId: ownerId } });

  yield put(push(withId(url, [account.id, 'accountId'])));
}

const watchAccountsPost = createQadraSaga(accountPost, { action: ACCOUNTS_POST._REQUEST });

function* accountRiskAppetiteProfilesGet({ payload: { accountId } }) {
  const { riskAppetiteProfiles } = yield call(apiAccountRiskAppetiteProfilesGet, accountId);
  yield put({
    type: ACCOUNT_RISK_APPETITE_PROFILES_GET._SUCCESS,
    payload: { accountId, riskAppetiteProfiles },
  });
}

const watchAccountRiskAppetiteProfilesGet = createQadraSaga(accountRiskAppetiteProfilesGet, {
  action: ACCOUNT_RISK_APPETITE_PROFILES_GET._REQUEST,
});

function* accountRiskAppetiteProfilesPostPatch({
  payload: { userId, accountId, accountRiskAppetiteProfileId, url, ...payload },
}) {
  const loggedUserId = yield select((s) => s.app.user.id);

  if (accountRiskAppetiteProfileId) {
    yield call(apiAccountRiskAppetiteProfilePatch, accountRiskAppetiteProfileId, payload);
  } else {
    yield call(apiAccountRiskAppetiteProfilePost, accountId, { ...payload, userId: loggedUserId });
  }

  yield accountRiskAppetiteProfilesGet({ payload: { accountId } });
  yield accountsGet({ payload: { userId: userId ?? loggedUserId } });

  if (url) yield put(push(url));
}

const watchAccountRiskAppetiteProfilesPostPatch = createQadraSaga(
  accountRiskAppetiteProfilesPostPatch,
  { action: ACCOUNT_RISK_APPETITE_PROFILES_POST_PATCH._REQUEST }
);

function* riskProfileSimulationGet({ payload: { accountId } }) {
  const { riskProfileCalculation } = yield call(apiAccountRiskProfileCalculationGet, accountId);
  yield put({
    type: ACCOUNT_SIMULATE_RISK_PROFILE._SUCCESS,
    payload: { accountId, riskProfileCalculation },
  });
}

const watchRiskProfileSimulationGet = createQadraSaga(riskProfileSimulationGet, {
  action: ACCOUNT_SIMULATE_RISK_PROFILE._REQUEST,
});

function* accountRiskProfilePost({ payload: { userId, accountId, riskProfile } }) {
  const loggedUserId = yield select((s) => s.app.user.id);
  yield call(apiAccountRiskProfilesPost, accountId, { riskProfileId: riskProfile.id });
  yield accountsGet({ payload: { userId: userId ?? loggedUserId } });
}

function* accountAcceptMifidProfile({
  payload: { accountId, investmentObjectiveId, riskProfile, url },
}) {
  yield accountRiskProfilePost({ payload: { accountId, riskProfile } });
  const acc = yield select(selectAccount, accountId);

  if (!acc.mifidAccepted) throw new Error('Mifid is not completed!');

  yield investmentObjectivePortfolioProposalsPost({
    payload: { investmentObjectiveId, type: 'auto' },
  });

  if (url) yield put(push(url));
}

const watchAccountAcceptMifidProfile = createQadraSaga(accountAcceptMifidProfile, {
  action: ACCOUNT_ACCEPT_MIFID_PROFILE._REQUEST,
});

function* accountLifestyleCostsGet({ payload: { accountId } }) {
  const { lifestyleCosts } = yield call(apiAccountLifestyleCostsGet, accountId);
  yield put({
    type: ACCOUNT_LIFESTYLE_COSTS_GET._SUCCESS,
    payload: { lifestyleCosts, accountId },
  });
}

const watchAccountLifestyleCostsGet = createQadraSaga(accountLifestyleCostsGet, {
  action: ACCOUNT_LIFESTYLE_COSTS_GET._REQUEST,
});

function* accountLifestyleCostsPostPatch({
  payload: { accountId, url, lifestyleCostsId, lifestyleCosts },
}) {
  const userId = yield select((s) => s.app.user.id);

  if (lifestyleCostsId) {
    yield call(apiAccountLifestyleCostsPatch, lifestyleCostsId, {
      userId,
      ...lifestyleCosts,
    });
  } else {
    yield call(apiAccountLifestyleCostsPost, accountId, {
      userId,
      ...lifestyleCosts,
    });
  }

  yield accountLifestyleCostsGet({ payload: { accountId } });

  if (url) yield put(push(url));
}

const watchAccountLifestyleCostsPostPatch = createQadraSaga(accountLifestyleCostsPostPatch, {
  action: ACCOUNT_LIFESTYLE_COSTS_POST_PATCH._REQUEST,
});

function* accountFinancialAssetsGet({ payload: { accountId } }) {
  const { financialAssets } = yield call(apiAccountFinancialAssetsGet, accountId);
  yield put({
    type: ACCOUNT_FINANCIAL_ASSETS_GET._SUCCESS,
    payload: { financialAssets, accountId },
  });
}

const watchAccountFinancialAssetsGet = createQadraSaga(accountFinancialAssetsGet, {
  action: ACCOUNT_FINANCIAL_ASSETS_GET._REQUEST,
});

function* accountFinancialAssetsPostPatch({
  payload: { accountId, url, financialAssetsId, financialAssets },
}) {
  const userId = yield select((s) => s.app.user.id);

  if (financialAssetsId) {
    yield call(apiAccountFinancialAssetsPatch, financialAssetsId, {
      userId,
      ...financialAssets,
    });
  } else {
    yield call(apiAccountFinancialAssetsPost, accountId, {
      userId,
      ...financialAssets,
    });
  }

  yield accountFinancialAssetsGet({ payload: { accountId } });

  if (url) yield put(push(url));
}

const watchAccountFinancialAssetsPostPatch = createQadraSaga(accountFinancialAssetsPostPatch, {
  action: ACCOUNT_FINANCIAL_ASSETS_POST_PATCH._REQUEST,
});

export function* accountFinancialExperiencesGet({ payload: { accountId } }) {
  const { financialExperiences } = yield call(apiAccountFinancialExperiencesGet, accountId);
  yield put({
    type: ACCOUNT_FINANCIAL_EXPERIENCES_GET._SUCCESS,
    payload: { financialExperiences, accountId },
  });
}

const watchAccountFinancialExperiencesGet = createQadraSaga(accountFinancialExperiencesGet, {
  action: ACCOUNT_FINANCIAL_EXPERIENCES_GET._REQUEST,
});

function* accountFinancialExperiencesPostPatch({
  payload: { accountId, url, financialExperiencesId, financialExperiences },
}) {
  const userId = yield select((s) => s.app.user.id);

  if (financialExperiencesId) {
    yield call(apiAccountFinancialExperiencesPatch, financialExperiencesId, {
      userId,
      ...financialExperiences,
    });
  } else {
    yield call(apiAccountFinancialExperiencesPost, accountId, {
      userId,
      ...financialExperiences,
    });
  }

  yield accountFinancialExperiencesGet({ payload: { accountId } });

  if (url) yield put(push(url));
}

const watchAccountFinancialExperiencesPostPatch = createQadraSaga(
  accountFinancialExperiencesPostPatch,
  { action: ACCOUNT_FINANCIAL_EXPERIENCES_POST_PATCH._REQUEST }
);

export function* accountOwnersGet({ payload: { accountId } }) {
  const { accountOwners } = yield call(apiAccountOwnersGet, accountId);

  const owners = accountOwners;
  yield put({
    type: ACCOUNT_OWNERS_GET._SUCCESS,
    payload: { owners, accountId },
  });
}

const watchAccountOwnersGet = createQadraSaga(accountOwnersGet, {
  action: ACCOUNT_OWNERS_GET._REQUEST,
});

export function* accountOwnersPatch({ payload: { accountId, id, url, ...rest } }) {
  yield call(apiAccountOwnersPatch, id, rest);
  yield accountOwnersGet({ payload: { accountId } });

  if (url) yield put(push(url));
}

const watchAccountOwnersPatch = createQadraSaga(accountOwnersPatch, {
  action: ACCOUNT_OWNERS_PATCH._REQUEST,
});

function* accountSignedInvestmentAgreementsGet({ payload: { accountId } }) {
  yield call(apiAccountSignedInvestmentAgreementsGet, accountId);
  // yield put({
  //   type: ACCOUNT_SIGNED_INVESTMENT_AGREEMENTS_GET._SUCCESS,
  //   payload: { investmentAgreements },
  // });
}

const watchAccountSignedInvestmentAgreementsGet = createQadraSaga(
  accountSignedInvestmentAgreementsGet,
  {
    action: ACCOUNT_SIGNED_INVESTMENT_AGREEMENTS_GET._REQUEST,
  }
);

function* accountInvestmentAgreementsGet() {
  const { investmentAgreements } = yield call(apiAccountInvestmentAgreementsGet);
  yield put({
    type: ACCOUNT_INVESTMENT_AGREEMENTS_GET._SUCCESS,
    payload: { investmentAgreements },
  });
}

const watchAccountInvestmentAgreementsGet = createQadraSaga(accountInvestmentAgreementsGet, {
  action: ACCOUNT_INVESTMENT_AGREEMENTS_GET._REQUEST,
});

function* accountInvestmentAgreementsPost({ payload: { userId, accountId, contractId, otp } }) {
  const loggedUserId = yield select((s) => s.app.user.id);
  yield call(apiAccountInvestmentAgreementsPost, accountId, { contractId, otp });
  yield accountsGet({ payload: { userId: userId ?? loggedUserId } });
}

const watchAccountInvestmentAgreementsPost = createQadraSaga(accountInvestmentAgreementsPost, {
  action: ACCOUNT_INVESTMENT_AGREEMENTS_POST._REQUEST,
  ...handleOtp(otpIds.signContracts),
});

function* accountInvestmentAgreementsPostOtp({ payload: { accountId, contractId, otp } }) {
  yield call(apiAccountInvestmentAgreementsPost, accountId, { contractId, otp });
}

const watchAccountInvestmentAgreementsPostOtp = createQadraSaga(
  accountInvestmentAgreementsPostOtp,
  {
    action: ACCOUNT_INVESTMENT_AGREEMENTS_POST_OTP._REQUEST,
    hideSpinner: true,
    ...handleOtp(otpIds.signContracts),
  }
);

export function* accountAmlProfilesGet({ payload: { accountId } }) {
  const { amlProfiles } = yield call(apiAccountAmlProfilesGet, accountId);
  yield put({
    type: ACCOUNT_AML_PROFILES_GET._SUCCESS,
    payload: { amlProfiles, accountId },
  });
}

const watchAccountAmlProfilesGet = createQadraSaga(accountAmlProfilesGet, {
  action: ACCOUNT_AML_PROFILES_GET._REQUEST,
});

function* accountAmlProfilesPostPatch({ payload: { accountId, url, amlProfileId, amlProfile } }) {
  const userId = yield select((s) => s.app.user.id);

  if (amlProfileId) {
    yield call(apiAccountAmlProfilesPatch, amlProfileId, {
      userId,
      ...amlProfile,
    });
  } else {
    yield call(apiAccountAmlProfilesPost, accountId, {
      userId,
      ...amlProfile,
    });
  }

  yield accountAmlProfilesGet({ payload: { accountId } });

  if (url) yield put(push(url));
}

const watchAccountAmlProfilesPostPatch = createQadraSaga(accountAmlProfilesPostPatch, {
  action: ACCOUNT_AML_PROFILES_POST_PATCH._REQUEST,
});

export function* accountTaxSchemesGet({ payload: { accountId } }) {
  const { taxSchemes } = yield call(apiAccountTaxSchemesGet, accountId);
  yield put({
    type: ACCOUNT_TAX_SCHEMES_GET._SUCCESS,
    payload: { taxSchemes, accountId },
  });
}

const watchAccountTaxSchemesGet = createQadraSaga(accountTaxSchemesGet, {
  action: ACCOUNT_TAX_SCHEMES_GET._REQUEST,
});

function* accountTaxSchemesPostPatch({ payload: { accountId, url, taxSchemeId, taxScheme } }) {
  const userId = yield select((s) => s.app.user.id);

  if (taxSchemeId) {
    yield call(apiAccountTaxSchemesPatch, taxSchemeId, {
      userId,
      ...taxScheme,
    });
  } else {
    yield call(apiAccountTaxSchemesPost, accountId, {
      userId,
      ...taxScheme,
    });
  }

  yield accountTaxSchemesGet({ payload: { accountId } });

  if (url) yield put(push(url));
}

const watchAccountTaxSchemesPostPatch = createQadraSaga(accountTaxSchemesPostPatch, {
  action: ACCOUNT_TAX_SCHEMES_POST_PATCH._REQUEST,
});

function* accountConfirmInfo({ payload: { accountId, url } }) {
  yield call(apiAccountConfirmInfo, accountId);
  yield accountGet({ payload: { accountId } });

  if (url) yield put(push(url));
}

const watchAccountConfirmInfo = createQadraSaga(accountConfirmInfo, {
  action: ACCOUNT_CONFIRM_INFO._REQUEST,
});

function* accountContractsGet({ payload: { accountId } }) {
  const { contracts } = yield call(apiAccountContractsGet, accountId);
  yield put({
    type: ACCOUNT_CONTRACTS_GET._SUCCESS,
    payload: { contracts, accountId },
  });
}

const watchAccountContractsGet = createQadraSaga(accountContractsGet, {
  action: ACCOUNT_CONTRACTS_GET._REQUEST,
});

function* accountContractPollingGet({ payload: { accountId } }) {
  const isDev = process.env.NODE_ENV === 'development';
  const pollingTime = 10000;

  let contracts = [];

  while (contracts.length === 0) {
    try {
      const res = yield call(apiAccountContractsGet, accountId);
      contracts = res.contracts;

      yield put({
        type: ACCOUNT_CONTRACTS_GET._SUCCESS,
        payload: { contracts, accountId },
      });
      yield delay(pollingTime);
    } catch (error) {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.warn(getServerErrorMessage(error));
      }
      yield delay(pollingTime);
    }
  }
}

function* watchAccountContractsPollingGet() {
  yield takeLatest(ACCOUNT_CONTRACTS_POLLING_GET._REQUEST, accountContractPollingGet);
}

function* accountContractsSignPostOtp({ payload: { accountId, ids, url, otp, alias } }) {
  yield call(apiAccountContractsSignPostOtp, accountId, ids, otp, alias);
  yield accountGet({ payload: { accountId } });
  yield accountContractsGet({ payload: { accountId } });
  yield put(actionModalOpen('ModalSignContractsSuccess'));

  if (url) yield put(push(url));
}

const watchAccountContractsSignPostOtp = createQadraSaga(accountContractsSignPostOtp, {
  action: ACCOUNT_CONTRACTS_SIGN_POST_OTP._REQUEST,
  hideSpinner: true,
  ...handleOtp(otpIds.signContracts),
});

// Account documents
function* accountDocumentsGet({ payload: { accountId } }) {
  const { documents } = yield call(apiAccountDocumentsGet, accountId);
  yield put({
    type: ACCOUNT_DOCUMENTS_GET._SUCCESS,
    payload: { documents, accountId },
  });
}

const watchAccountDocumentsGet = createQadraSaga(accountDocumentsGet, {
  action: ACCOUNT_DOCUMENTS_GET._REQUEST,
});

export function* accountDocumentsPost({ payload, showSpinner }) {
  const { accountId, form, backUrl } = payload;
  const { file, type } = form;

  if (!file) return;

  try {
    const formData = new FormData();
    formData.append('file', new File([file || file], file.name || file));
    formData.append('type', type);

    if (showSpinner) yield put(actionSpinnerOpen());

    const result = yield call(apiAccountDocumentsPost, accountId, { file: formData });
    yield accountDocumentsGet({ payload: { accountId } });

    if (backUrl) yield put(push(backUrl));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('saga > fileUpload > fileUpload', e.message);
    if (!showSpinner) throw e;
  } finally {
    if (showSpinner) yield put(actionSpinnerClose());
  }
}

const watchAccountDocumentsPost = createQadraSaga(accountDocumentsPost, {
  action: ACCOUNT_DOCUMENTS_POST._REQUEST,
});

function* accountAumsGet({ payload: { accountId, startDate, endDate } }) {
  const { aums } = yield call(apiAccountAumsGet, { accountId, startDate, endDate });
  yield put({
    type: ACCOUNT_AUMS_GET._SUCCESS,
    payload: { accountId, aums },
  });
}

const watchAccountAumsGet = createQadraSaga(accountAumsGet, {
  action: ACCOUNT_AUMS_GET._REQUEST,
});

export default [
  watchAccountGet,
  watchAccountsGet,
  watchAccountsPost,
  watchAccountLifestyleCostsGet,
  watchAccountLifestyleCostsPostPatch,
  watchAccountFinancialAssetsGet,
  watchAccountFinancialAssetsPostPatch,
  watchAccountFinancialExperiencesGet,
  watchAccountFinancialExperiencesPostPatch,
  watchAccountRiskAppetiteProfilesGet,
  watchAccountRiskAppetiteProfilesPostPatch,
  watchRiskProfileSimulationGet,
  watchAccountAcceptMifidProfile,
  watchAccountInvestmentAgreementsGet,
  watchAccountInvestmentAgreementsPost,
  watchAccountInvestmentAgreementsPostOtp,
  watchAccountSignedInvestmentAgreementsGet,
  watchAccountOwnersGet,
  watchAccountOwnersPatch,
  watchAccountAmlProfilesGet,
  watchAccountAmlProfilesPostPatch,
  watchAccountTaxSchemesGet,
  watchAccountTaxSchemesPostPatch,
  watchAccountConfirmInfo,
  watchAccountContractsGet,
  watchAccountContractsPollingGet,
  watchAccountContractsSignPostOtp,
  watchAccountDocumentsGet,
  watchAccountDocumentsPost,
  watchAccountAumsGet,
];
